/**
 * D-day 산정의 달력 기준 — **Asia/Seoul 고정**. (TRAD-2349 / TRAD-2317, ~~정책 2316~~ → TRAD-2352 결정 A —
 * 2316 은 «현지시간 권장»이라 이 구현과 반대였고 이 인용은 오기였다. 2026-10-09 davidyoon 이 «D-day·마감 둘 다 KST» 로 결정)
 *
 * ## 왜 "날짜 경계"인가
 * 이전 구현들은 두 **시점** 사이의 경과 시간을 24시간으로 나눴다. 즉 "며칠 남았나"가 아니라
 * "24시간이 몇 번 남았나"였고, 유저의 달력 감각과 어긋났다:
 *  - 지금 10:00 / 내일 09:00 마감 → 23시간이라 `D-Day` (실제로는 내일이므로 `D-1` 이어야 한다)
 *  - 지금 23:00 / 내일 01:00 마감 → 2시간인데도 날짜가 넘어가므로 `D-1` 이 맞다
 * 이제 경계는 24시간 간격이 아니라 **자정**이다.
 *
 * ## 왜 "KST 고정"인가 — 🔴 존을 파라미터로 열지 않는다
 * 보는 사람의 로컬 존이 아니다. 마켓 마감·청약 마감은 운영이 **한국 벽시계로 정하는 값**이라,
 * 기기 존에 따라 같은 마켓이 누구에겐 `D-1`, 누구에겐 `D-Day` 로 보이면 안 된다
 * (해외 체류 유저·기기 존 오설정). 존을 주입 가능하게 만들면 **호출부가 로컬 존을 넘기는
 * 순간 이 고정이 깨지므로**, 애초에 인자를 두지 않는다. android `MarketCardUtils.kt` 의
 * `DDAY_ZONE` 과 같은 원칙이다.
 *
 * ⚠️ **표시용 날짜 문자열은 정책이 다르다.** `formatMarketDateTime`(TRAD-2299)은 의도적으로
 * **보는 사람의 로컬 존**으로 렌더한다. "존을 통일하자"고 두 정책을 묶으려 들지 말 것 —
 * 산정(여기)은 KST, 표시는 로컬이 각각 맞다.
 *
 * ## 왜 `Intl` 이 아니라 고정 오프셋 산술인가
 * Asia/Seoul 은 **DST 가 없다**(1988 서울올림픽 이후 폐지, 이후 항상 UTC+09:00). 마감일·청약
 * 종료일은 전부 현재~미래 값이므로 고정 오프셋이 IANA 존과 동일한 결과를 준다. 그 대신
 * `getFullYear()`·`getMonth()`·`getDate()` 같은 **로컬 존 의존 API 를 한 번도 쓰지 않는다** —
 * 이 파일에 로컬 존 메서드가 등장하지 않는다는 것 자체가 존 고정의 구조적 보증이다.
 * (`staleness.ts` 의 교훈: 결과 비교만으로는 KST 머신에서 로컬 존 구현이 거짓 초록으로 통과한다.)
 *
 * 전부 순수 함수 — 입력만으로 출력이 정해지고, 실행 환경 타임존에 좌우되지 않는다.
 */

const DAY_MS = 86_400_000;

/** Asia/Seoul = UTC+09:00. 상수로 박는다(위 주석의 "파라미터로 열지 않는다"). */
export const KST_UTC_OFFSET_MINUTES = 540;

const KST_OFFSET_MS = KST_UTC_OFFSET_MINUTES * 60_000;

/**
 * 시점(instant) → **KST 달력 날짜의 일련번호**(1970-01-01 KST 를 0 으로 하는 정수).
 *
 * 두 시점의 인덱스를 빼면 "KST 자정을 몇 번 건너뛰는가" = D-day 일수가 된다.
 * 날짜 자체가 아니라 인덱스를 돌려주는 이유는, 월·연 경계를 호출부가 계산하지 않게 하기 위함이다.
 *
 * @returns 파싱 불가(Invalid Date)면 `null`. 🔴 폴백 숫자를 만들지 않는다 — 폴백을 주면
 *          호출부가 "오늘"과 "값이 없음"을 구분하지 못해 없는 D-day 를 그린다.
 */
export const toKstDayIndex = (instant: Date): number | null => {
  const ms = instant.getTime();
  if (Number.isNaN(ms)) return null;
  return Math.floor((ms + KST_OFFSET_MS) / DAY_MS);
};

/** `YYYY-MM-DD` — 시각·오프셋이 없는 **날짜만** 형식. */
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * `"YYYY-MM-DD"` 문자열 → KST 달력 날짜 일련번호([toKstDayIndex] 와 같은 눈금).
 *
 * 🔴 이 값은 **시점이 아니라 벽시계 날짜**다(청약 `applyEndDate` 처럼 서버가 날짜만 주는 필드).
 * 그래서 존 변환 대상이 아니며, 적힌 날짜를 그대로 KST 날짜로 읽는다.
 * `new Date("2026-03-26")` 처럼 파싱해 로컬 존 메서드로 되꺼내면 UTC 뒤쪽 사용자에게
 * 하루가 밀린다 — 그 경로를 아예 만들지 않으려고 문자열에서 직접 숫자를 뽑는다.
 *
 * @returns 형식이 아니거나 **달력에 없는 날짜**(`2026-02-31`)면 `null`.
 *          `Date.UTC` 는 그런 값을 조용히 3/3 으로 굴리므로 왕복 검산으로 막는다.
 */
export const toKstDayIndexFromDateOnly = (value: string): number | null => {
  const matched = DATE_ONLY_PATTERN.exec(value.trim());
  if (!matched) return null;

  const year = Number(matched[1]);
  const month = Number(matched[2]);
  const day = Number(matched[3]);

  const ms = Date.UTC(year, month - 1, day);
  const roundTrip = new Date(ms);

  if (
    roundTrip.getUTCFullYear() !== year
    || roundTrip.getUTCMonth() !== month - 1
    || roundTrip.getUTCDate() !== day
  ) {
    return null;
  }

  return ms / DAY_MS;
};

/**
 * KST 자정 경계로 센 남은 일수. 양수 = 아직 남음, 0 = 오늘, 음수 = 지남.
 *
 * @returns 어느 쪽이든 판정 불가면 `null`.
 */
export const kstDaysUntil = (targetDayIndex: number | null, nowDayIndex: number | null): number | null => (
  targetDayIndex === null || nowDayIndex === null ? null : targetDayIndex - nowDayIndex
);

/**
 * 시점 → **KST 벽시계 구성요소**. 내부 헬퍼.
 *
 * 🔴 `+09:00` 만큼 옮긴 뒤 **UTC 게터로만** 읽는다. `getFullYear()` 같은 로컬 존 게터를 한 번이라도
 * 쓰면 이 파일의 존 고정 보증(파일 최상단 주석)이 무너진다.
 */
const kstWallClock = (instant: Date) => {
  const ms = instant.getTime();
  if (Number.isNaN(ms)) return null;

  const shifted = new Date(ms + KST_OFFSET_MS);

  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
  };
};

const pad2 = (value: number): string => String(value).padStart(2, '0');

/**
 * 시점 → **KST 기준 `YYYY-MM-DD`**. `<input type="date">` 의 `min` 에 쓴다. (TRAD-2361)
 *
 * 이전 구현은 `getFullYear()/getMonth()/getDate()` 로 "오늘"을 **실행 환경 타임존**에서 뽑았다.
 * KST 보다 뒤쪽 존(예: 뉴욕)에서는 로컬 오늘이 KST 로는 이미 어제라, 달력이 **KST 기준 지난
 * 날짜를 고를 수 있게** 열려 있었다. 마감 일시는 서버가 KST 벽시계로 판정하므로 min 도 KST 여야 한다.
 *
 * @returns 파싱 불가(Invalid Date)면 `null` — 폴백 날짜를 지어내지 않는다(없는 하한을 그리지 않는다).
 */
export const toKstDateString = (instant: Date): string | null => {
  const parts = kstWallClock(instant);
  if (parts === null) return null;

  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
};

/**
 * 시점 → **KST 기준 `YYYY-MM-DDTHH:mm`**. `<input type="datetime-local">` 의 `min` 에 쓴다. (TRAD-2361)
 *
 * 초는 붙이지 않는다 — datetime-local 의 표준 값 형식이고, 초까지 주면 브라우저가 초 입력칸을 연다.
 */
export const toKstDateTimeString = (instant: Date): string | null => {
  const parts = kstWallClock(instant);
  if (parts === null) return null;

  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}T${pad2(parts.hour)}:${pad2(parts.minute)}`;
};

/**
 * 지금부터 **다음 KST 자정까지 남은 밀리초**. (TRAD-2356)
 *
 * D-day 는 렌더 시점에만 계산되므로, 화면을 열어 둔 채 자정을 넘기면 `D-1` 이 종일 남는다.
 * 자정에 다시 계산하려면 "언제 깨어날 것인가"가 필요하고, 그 값이 여기다.
 *
 * 🔴 **순수 함수로 남긴다** — 시각을 인자로 받고 `setTimeout` 을 알지 못한다. 스케줄링(사이드
 * 이펙트)은 훅 경계(`useKstToday`)로 밀어내고, 경계 계산은 가짜 타이머 없이 단위 테스트한다.
 *
 * 🔴 **0 을 돌려주지 않는다.** 정확히 자정인 순간의 "다음 자정"은 24시간 뒤다. 여기서 0 을 주면
 * 훅이 지연 0 짜리 타이머를 무한히 재예약해 렌더 루프가 된다.
 *
 * @returns 1 ~ 86_400_000. 파싱 불가(Invalid Date)면 `null` — 폴백 지연을 지어내지 않는다.
 *          (폴백을 주면 "언제 깨어날지 모른다"가 "곧 깨어난다"로 둔갑한다.)
 */
export const msUntilNextKstMidnight = (now: Date): number | null => {
  const ms = now.getTime();
  if (Number.isNaN(ms)) return null;

  // JS 의 `%` 는 피연산자가 음수면 음수를 낸다(epoch 이전 시점). 항상 양수 나머지로 정규화한다.
  const sinceKstMidnight = (((ms + KST_OFFSET_MS) % DAY_MS) + DAY_MS) % DAY_MS;

  return DAY_MS - sinceKstMidnight;
};
